import { ReactNode, useEffect } from 'react';
import LoadingPage from 'pages/LoadingPage';
import { useParams } from 'react-router';
import { AssociateParams } from 'types/RouteParams';
import { useMutation } from '@apollo/client';
import {ACCEPT_RELATIONSHIP_MUTATION, AcceptRelationshipError, type AcceptRelationshipOutput} from 'graphql/relationship';
import {RelationshipState} from 'types/Relationship';
import { FormattedMessage } from 'react-intl';
import { emptyFunction } from 'utils/functions';
import useStyles from './AcceptRelationship.styles';
import { Alert, Grid } from '@mui/material';
import { ArdoizBrand, FilledButton } from 'components';
import { navigateToChannelRoute, navigate } from 'utils/history';
import Route from 'routes/Routes';

type AcceptRelationshipContainerProps = {
  children: ReactNode;
};

const AcceptRelationshipContainer = ({
  children,
}: AcceptRelationshipContainerProps) => {
  const classes = useStyles();

  const handleClick = () => navigate(Route.HOME);

  return (
    <>
      <div className={classes.logo}>
        <ArdoizBrand onClick={handleClick} />
      </div>

      <div className={classes.container}>{children}</div>
    </>
  );
};

type AcceptRelationshipErrorAlertProps = {
  error: ReactNode;
};

const AcceptRelationshipErrorAlert = ({ error }: AcceptRelationshipErrorAlertProps) => (
  <AcceptRelationshipContainer>
    <Alert severity="error">{error}</Alert>
  </AcceptRelationshipContainer>
);

type AcceptAssociateSuccessProps = {
  channel: string;
};

const AcceptAssociateSuccess = ({
  channel,
}: AcceptAssociateSuccessProps): JSX.Element => {
  const classes = useStyles();

  const handleClick = () => navigateToChannelRoute(channel, Route.HOME);
  
  return (
    <AcceptRelationshipContainer>
      <Grid container direction="column">
        <Grid item>
          <div className={classes.successContainer}>
            <Alert severity="success">
              <FormattedMessage id="relationship.accept.success" />
            </Alert>
          </div>
        </Grid>

        <Grid className={classes.successDescriptionContainer} item>
          <FormattedMessage id="relationship.accept.success.description" />
        </Grid>

        <Grid className={classes.successButtonContainer} item>
          <FilledButton
            color="violet"
            onClick={handleClick}
          >
            <FormattedMessage id="button.backToHome" />
          </FilledButton>
        </Grid>
      </Grid>
    </AcceptRelationshipContainer>
  );
};

const AcceptRelationship = (): JSX.Element | null => {
  const { uuid } = useParams<AssociateParams>();
  const [acceptRelationship, { called, data, error, loading }] =
    useMutation<AcceptRelationshipOutput>(ACCEPT_RELATIONSHIP_MUTATION);

  useEffect(() => {
    if (!called) {
      acceptRelationship({
        variables: {
          uuid,
        },
      })
      .then(emptyFunction)
      .catch(emptyFunction); // TODO
    }
  });

  if (loading) {
    return <LoadingPage />;
  }

  if (
    data?.acceptRelationship?.relationship?.state ===
    RelationshipState.CONNECTED
  ) {
    return (
      <AcceptAssociateSuccess
        channel={data.acceptRelationship.relationship.channel}
      />
    );
  }

  if (error) {
    const networkError = error.networkError;

    if (
      networkError &&
      'statusCode' in networkError &&
      networkError?.statusCode === 401
    ) {
      return null;
    }

    return (
      <AcceptRelationshipErrorAlert
        error={<FormattedMessage id="relationship.error.other" />}
      />
    );
  }

  switch (data?.acceptRelationship?.errorReason) {
    case AcceptRelationshipError.ALREADY_ACCEPTED:
      return (
        <AcceptRelationshipErrorAlert
          error={<FormattedMessage id="relationship.error.already_accepted" />}
        />
      );

    case AcceptRelationshipError.NOT_FOUND:
      return (
        <AcceptRelationshipErrorAlert
          error={<FormattedMessage id="relationship.error.not_found" />}
        />
      );

    case AcceptRelationshipError.OTHER:
      return (
        <AcceptRelationshipErrorAlert
          error={<FormattedMessage id="relationship.error.other" />}
        />
      );
    default:
      break;
  }

  return null;
};

export default AcceptRelationship;
